/**
 * AutoRateToggle Component
 * Toggle for enabling/disabling automatic rate assignment by location
 * Supports inheritance for groups and units
 */

import { useEffect, useState } from 'react';
import { MapPin, Info, Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { useAutoRateSettings } from '@/hooks/useAutoRateSettings';
import type { AutoRateEntityType, AutoRateSetting } from '@/types/rates';

interface AutoRateToggleProps {
  /** Entity type */
  entityType: AutoRateEntityType;
  /** Entity ID */
  entityId: string;
  /** Whether to show as compact inline toggle */
  compact?: boolean;
  /** Callback when value changes */
  onChange?: (value: boolean | null) => void;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Show preview for units */
  showPreview?: boolean;
}

/**
 * Auto-rate toggle component with inheritance support
 */
export function AutoRateToggle({
  entityType,
  entityId,
  compact = false,
  onChange,
  disabled = false,
  showPreview = false,
}: AutoRateToggleProps) {
  const {
    setting,
    preview,
    isLoading,
    error,
    fetchSetting,
    updateSetting,
    fetchPreview,
  } = useAutoRateSettings();

  const [localValue, setLocalValue] = useState<boolean | null>(null);

  // Fetch setting on mount
  useEffect(() => {
    if (entityId) {
      fetchSetting(entityType, entityId);
      if (showPreview && entityType === 'unit') {
        fetchPreview(entityId);
      }
    }
  }, [entityType, entityId, fetchSetting, fetchPreview, showPreview]);

  // Update local value when setting changes
  useEffect(() => {
    if (setting) {
      setLocalValue(setting.enabled);
    }
  }, [setting]);

  const handleChange = async (newValue: boolean | null) => {
    setLocalValue(newValue);
    onChange?.(newValue);

    // Update on server
    await updateSetting(entityType, entityId, newValue);
  };

  // For companies: simple switch (no inheritance)
  if (entityType === 'company') {
    return (
      <div className={compact ? 'flex items-center gap-2' : 'space-y-2'}>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-cyan-500" />
          <Label htmlFor="auto-rate-toggle" className="font-medium">
            Auto-Rate by Location
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p className="text-sm">
                  When enabled, units in this company will automatically be assigned
                  energy rates based on their location (postal code or state).
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <Switch
            id="auto-rate-toggle"
            checked={localValue === true}
            onCheckedChange={(checked) => handleChange(checked)}
            disabled={disabled || isLoading}
          />
        )}

        {error && (
          <p className="text-xs text-destructive">{error.message}</p>
        )}
      </div>
    );
  }

  // For groups and units: dropdown with inherit option
  return (
    <div className={compact ? 'flex items-center gap-3' : 'space-y-2'}>
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-cyan-500" />
        <Label htmlFor="auto-rate-select" className="font-medium">
          Auto-Rate by Location
        </Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <p className="text-sm">
                {entityType === 'group'
                  ? 'Controls automatic rate assignment for units in this group. Select "Inherit" to use the company setting.'
                  : 'Controls automatic rate assignment for this unit based on its location. Select "Inherit" to use the group/company setting.'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      ) : (
        <div className="flex flex-col gap-1">
          <Select
            value={localValue === null ? 'inherit' : localValue ? 'enabled' : 'disabled'}
            onValueChange={(value) => {
              const newValue = value === 'inherit' ? null : value === 'enabled';
              handleChange(newValue);
            }}
            disabled={disabled || isLoading}
          >
            <SelectTrigger id="auto-rate-select" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="inherit">
                <span className="flex items-center gap-2">
                  Inherit
                  {setting && (
                    <span className="text-xs text-muted-foreground">
                      ({setting.resolvedValue ? 'On' : 'Off'})
                    </span>
                  )}
                </span>
              </SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
              <SelectItem value="enabled">Enabled</SelectItem>
            </SelectContent>
          </Select>

          {setting && localValue === null && (
            <p className="text-xs text-muted-foreground">
              Inheriting from {setting.resolvedFrom}: {setting.resolvedValue ? 'Enabled' : 'Disabled'}
            </p>
          )}
        </div>
      )}

      {/* Preview for units */}
      {showPreview && entityType === 'unit' && preview && (
        <div className="mt-2 p-2 bg-muted/50 rounded-md text-xs">
          {preview.previewAutoRate ? (
            <>
              <p className="font-medium text-cyan-600 dark:text-cyan-400">
                Preview: {preview.previewAutoRate.providerName}
              </p>
              <p className="text-muted-foreground">
                Rate: {preview.previewAutoRate.rateName}
              </p>
              <p className="text-muted-foreground">
                Matched by: {preview.previewAutoRate.matchType === 'postal_code' ? 'ZIP Code' : 'State'} ({preview.previewAutoRate.matchValue})
              </p>
              {preview.wouldChange && (
                <p className="text-amber-600 dark:text-amber-400 mt-1">
                  Enabling would change the rate
                </p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">
              No matching provider found for this location
            </p>
          )}
        </div>
      )}

      {/* Rate override warning for units */}
      {entityType === 'unit' && setting?.rateOverride && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Rate override is enabled. Auto-rate will not apply.
        </p>
      )}

      {error && (
        <p className="text-xs text-destructive">{error.message}</p>
      )}
    </div>
  );
}

/**
 * Inline auto-rate badge for display in tables/lists
 */
export function AutoRateBadge({ setting }: { setting: AutoRateSetting | null }) {
  if (!setting) return null;

  const isEnabled = setting.resolvedValue;
  const isInherited = setting.enabled === null;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isEnabled
          ? 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400'
          : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
      }`}
    >
      <MapPin className="w-3 h-3" />
      {isEnabled ? 'Auto' : 'Manual'}
      {isInherited && (
        <span className="text-[10px] opacity-70">(inherited)</span>
      )}
    </span>
  );
}

export default AutoRateToggle;
